/**
 * PhotoViewer — 4 states: default, info-panel, zoom, delete-confirm.
 *
 * Figma sources:
 *  - default:       40000026:11835
 *  - info-panel:    40000026:11861
 *  - zoom:          40000026:11889
 *  - delete-confirm: 40000026:11918
 *
 * Layout (PO-verified 2026-05-22):
 *  - imageContainer 402×664 + statusbar 96
 *  - floating AnimateFloatingCTA 134×32 bottom-center INSIDE imageContainer
 *  - 32×32 info button top-left (glass-12)
 *  - 32×32 remove button top-right (glass-12)
 *  - 8 reference thumbnails 42×42 r8 (if refs non-empty)
 *  - PromptBlock (if userPrompt non-empty)
 *  - ActionRow 370×48 (3 buttons)
 *
 * Received as prop: item (gallery item object)
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/primitives';
import AnimateFloatingCTA from '../components/viewers/PhotoViewer/AnimateFloatingCTA';
import ActionRow from '../components/viewers/PhotoViewer/ActionRow';
import InfoPanel from '../components/viewers/PhotoViewer/InfoPanel';
import DeleteConfirmModal from '../components/viewers/PhotoViewer/DeleteConfirmModal';
import PromptBlock from '../components/viewers/PhotoViewer/PromptBlock';
import PhotoToVideoSheet from '../components/viewers/PhotoToVideoSheet';
import {
  useProfileQuery,
  useSubscriptionCurrentQuery,
} from '../core/queries/account';
import {
  useDeleteGalleryItemMutation,
  usePrepareShareMutation,
} from '../core/queries/mutations';
import {
  resolveActivePlanKey,
  resolveCreditsTotal,
} from '../core/accountPresentation';
import {
  VIEWER_ICON_BUTTON_STYLE,
  VIEWER_INLINE_CONTROL_INSET,
  VIEWER_TOP_CONTROL_OFFSET,
} from '../components/viewers/viewerLayout';
import { useAuthStore } from '../store/authStore';
import {
  downloadAsset,
  haptic,
  session,
  sharePreparedMessage,
  showAlert,
} from '../platform';
import { parsePhotoPresetToken } from '../core/photoPresetId';
import useConsentGuard from '../hooks/useConsentGuard';

const triggerSwipeHaptic = () => {
  if (haptic('light')) return;

  if (navigator.vibrate) navigator.vibrate(10);
};

function normaliseRepeatPresetId(value) {
  return parsePhotoPresetToken(value).presetId;
}

function normaliseRepeatQuality(value, presetValue) {
  if (value) return value;
  return parsePhotoPresetToken(presetValue).quality;
}

function getContainedImageSize(containerWidth, containerHeight, imageWidth, imageHeight) {
  if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) {
    return { width: 0, height: 0 };
  }

  const ratio = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
  return {
    width: imageWidth * ratio,
    height: imageHeight * ratio,
  };
}

function clampOffsetToBounds(offset, bounds) {
  if (!Number.isFinite(bounds.x) || !Number.isFinite(bounds.y)) return offset;

  return {
    x: Math.min(bounds.x, Math.max(-bounds.x, offset.x)),
    y: Math.min(bounds.y, Math.max(-bounds.y, offset.y)),
  };
}

function isInteractiveViewerTarget(target) {
  return target instanceof Element
    && Boolean(target.closest('button,a,input,textarea,select,[role="button"]'));
}

export default function PhotoViewer({ item, onSwipeNext, onSwipePrev, onDeleteSuccess }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const [showInfo, setShowInfo] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [imageIsLoading, setImageIsLoading] = useState(Boolean(item?.imageUrl));
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [bottomPanelHeight, setBottomPanelHeight] = useState(0);
  const imageContainerRef = useRef(null);
  const imageMetricsRef = useRef({ width: 0, height: 0 });
  const bottomPanelRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  const pinchRef = useRef({
    active: false,
    startDistance: 0,
    startScale: 1,
  });
  const panRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    startOffset: { x: 0, y: 0 },
  });
  const swipeRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
  });
  // [07.4] PhotoToVideoSheet — opens on AnimateFloatingCTA tap.
  const [showReviveSheet, setShowReviveSheet] = useState(false);
  const deleteGalleryItemMutation = useDeleteGalleryItemMutation();
  const prepareShareMutation = usePrepareShareMutation();
  const shareInFlightRef = useRef(false);
  const authUser = useAuthStore((s) => s.user);
  const authCredits = useAuthStore((s) => s.credits);
  const profileQuery = useProfileQuery({ retry: false });
  const subscriptionQuery = useSubscriptionCurrentQuery({ retry: false });
  const profileUser = profileQuery.data?.user ?? profileQuery.data ?? null;
  const accountUser = profileUser ?? authUser ?? null;
  const userPlan = resolveActivePlanKey({
    subscription: subscriptionQuery.data?.subscription ?? null,
    user: accountUser,
  });
  const balance = resolveCreditsTotal({
    credits: profileUser ? null : authCredits,
    user: accountUser,
  });

  const {
    id,
    imageUrl,
    userPrompt,
    aspectRatio,
    quality,
    model,
    referenceImageUrls,
    presetId,
  } = item || {};

  const refs = Array.isArray(referenceImageUrls) ? referenceImageUrls : [];
  const hasPrompt = Boolean(userPrompt);
  const isZoomed = zoomScale > 1.01;
  const controlsVisibilityClass = isZoomed
    ? 'pointer-events-none opacity-0'
    : 'opacity-100';

  useEffect(() => {
    imageMetricsRef.current = { width: 0, height: 0 };
    setImageIsLoading(Boolean(imageUrl));
    setZoomScale(1);
    setZoomOffset({ x: 0, y: 0 });
    setShowInfo(false);
    setShowDeleteModal(false);
    setIsDeleting(false);
    setDeleteError(null);
  }, [id, imageUrl]);

  const getPanBounds = (scale = zoomScale) => {
    const container = imageContainerRef.current;
    const rect = container?.getBoundingClientRect();
    const imageMetrics = imageMetricsRef.current;

    if (!rect?.width || !rect?.height || !imageMetrics.width || !imageMetrics.height) {
      return { x: Infinity, y: Infinity };
    }

    const contained = getContainedImageSize(
      rect.width,
      rect.height,
      imageMetrics.width,
      imageMetrics.height,
    );

    return {
      x: Math.max(0, (contained.width * scale - rect.width) / 2),
      y: Math.max(0, (contained.height * scale - rect.height) / 2),
    };
  };

  const clampZoomOffset = (offset, scale = zoomScale) => {
    if (scale <= 1.01) return { x: 0, y: 0 };
    return clampOffsetToBounds(offset, getPanBounds(scale));
  };

  useLayoutEffect(() => {
    const panel = bottomPanelRef.current;
    if (!panel) return undefined;

    const updateHeight = () => setBottomPanelHeight(panel.offsetHeight || 0);
    updateHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [refs.length, userPrompt, deleteError, isZoomed]);

  const clampZoom = (value) => Math.min(4, Math.max(1, value));

  const handleImageLoad = (event) => {
    imageMetricsRef.current = {
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    };
    setImageIsLoading(false);
    setZoomOffset((offset) => clampZoomOffset(offset));
  };

  const handleImageError = () => {
    setImageIsLoading(false);
  };

  const toggleZoom = () => {
    setShowInfo(false);
    setZoomScale((scale) => {
      if (scale > 1.01) {
        setZoomOffset({ x: 0, y: 0 });
        return 1;
      }
      return 2.35;
    });
  };

  const distanceBetweenTouches = (touches) => {
    const [a, b] = touches;
    if (!a || !b) return 0;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };

  const handleTouchStart = (event) => {
    if (event.touches.length === 1 && !isZoomed) {
      const touch = event.touches[0];
      swipeRef.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
      };
      return;
    }

    if (event.touches.length === 1 && isZoomed) {
      const touch = event.touches[0];
      panRef.current = {
        active: true,
        moved: false,
        startX: touch.clientX,
        startY: touch.clientY,
        startOffset: zoomOffset,
      };
      return;
    }

    if (event.touches.length !== 2) return;

    pinchRef.current = {
      active: true,
      startDistance: distanceBetweenTouches(event.touches),
      startScale: zoomScale,
    };
    setShowInfo(false);
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 1 && panRef.current.active && isZoomed) {
      event.preventDefault();
      const touch = event.touches[0];
      const dx = touch.clientX - panRef.current.startX;
      const dy = touch.clientY - panRef.current.startY;
      panRef.current.moved = Math.abs(dx) > 4 || Math.abs(dy) > 4;
      setZoomOffset(clampZoomOffset({
        x: panRef.current.startOffset.x + dx,
        y: panRef.current.startOffset.y + dy,
      }));
      return;
    }

    if (event.touches.length !== 2 || !pinchRef.current.active) return;

    event.preventDefault();
    const nextDistance = distanceBetweenTouches(event.touches);
    if (!pinchRef.current.startDistance || !nextDistance) return;

    const nextScale = clampZoom(
      (pinchRef.current.startScale * nextDistance) / pinchRef.current.startDistance,
    );
    setZoomScale(nextScale);
    setZoomOffset((offset) => clampZoomOffset(offset, nextScale));
  };

  const handleTouchEnd = (event) => {
    if (panRef.current.active) {
      const didMove = panRef.current.moved;
      panRef.current.active = false;
      panRef.current.moved = false;
      if (didMove) return;
    }

    if (pinchRef.current.active) {
      pinchRef.current.active = false;
      setZoomScale((scale) => clampZoom(scale));
      setZoomOffset((offset) => clampZoomOffset(offset));
      return;
    }

    if (event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];
    if (swipeRef.current.active && !isZoomed) {
      const dx = touch.clientX - swipeRef.current.startX;
      const dy = touch.clientY - swipeRef.current.startY;
      swipeRef.current.active = false;

      if (Math.abs(dx) > 64 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        event.preventDefault();
        const swipeHandler = dx < 0 ? onSwipeNext : onSwipePrev;
        if (!swipeHandler) return;

        setShowInfo(false);
        triggerSwipeHaptic();
        swipeHandler();
        return;
      }
    }

    if (isInteractiveViewerTarget(event.target)) return;

    const isZoomSurface = event.target instanceof Element
      && event.target.closest('[data-testid="photo-image-container"]');
    if (!isZoomSurface) return;

    const now = Date.now();
    const lastTap = lastTapRef.current;
    const moved = Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y);

    if (now - lastTap.time < 280 && moved < 28) {
      event.preventDefault();
      toggleZoom();
      lastTapRef.current = { time: 0, x: 0, y: 0 };
      return;
    }

    lastTapRef.current = { time: now, x: touch.clientX, y: touch.clientY };
  };

  const handleMouseDown = (event) => {
    if (!isZoomed) return;
    event.preventDefault();
    panRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: zoomOffset,
    };
  };

  const handleMouseMove = (event) => {
    if (!panRef.current.active || !isZoomed) return;
    const dx = event.clientX - panRef.current.startX;
    const dy = event.clientY - panRef.current.startY;
    panRef.current.moved = Math.abs(dx) > 4 || Math.abs(dy) > 4;
    setZoomOffset(clampZoomOffset({
      x: panRef.current.startOffset.x + dx,
      y: panRef.current.startOffset.y + dy,
    }));
  };

  const handleMouseUp = () => {
    panRef.current.active = false;
    panRef.current.moved = false;
  };

  const handleDoubleClick = (event) => {
    if (isInteractiveViewerTarget(event.target)) return;
    event.preventDefault();
    toggleZoom();
  };

  if (!item) return null;

  // --- Actions ---

  const handleDownload = () => {
    if (downloadAsset({ url: imageUrl, filename: `photo_${id}.jpg` })) return;

    if (imageUrl) {
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = `photo_${id}.jpg`;
      a.click();
    }
  };

  const handleShare = async () => {
    if (!id || shareInFlightRef.current) return;
    shareInFlightRef.current = true;
    try {
      const result = await prepareShareMutation.mutateAsync({
        generationId: id,
        language: i18n.resolvedLanguage || i18n.language,
      });
      if (!sharePreparedMessage(result?.preparedMessageId)) {
        throw new Error('Telegram shareMessage is unavailable');
      }
    } catch {
      showAlert(t('viewer.shareFailed'));
    } finally {
      shareInFlightRef.current = false;
    }
  };

  const handleCopyPrompt = async () => {
    if (!hasPrompt) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(userPrompt);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = userPrompt;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand?.('copy');
      document.body.removeChild(textarea);
    }
  };

  const performRepeat = () => {
    // PO-verified session flow (Prompt.jsx already reads these keys)
    if (userPrompt) session.setItem('repeatPrompt', userPrompt);
    if (aspectRatio) session.setItem('repeatAspectRatio', aspectRatio);
    const repeatQuality = normaliseRepeatQuality(quality, presetId);
    if (repeatQuality) session.setItem('repeatQuality', repeatQuality);
    if (refs.length > 0) session.setItem('repeatReference', JSON.stringify(refs));
    navigate(`/prompt/${normaliseRepeatPresetId(presetId)}`);
  };
  const handleRepeat = () => requireConsent(performRepeat, 'generate');

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteGalleryItemMutation.mutateAsync(id);
      setShowDeleteModal(false);
      setIsDeleting(false);
      setDeleteError(null);
      if (onDeleteSuccess) {
        onDeleteSuccess(id);
        return;
      }
      navigate('/gallery');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // Endpoint not yet implemented — show graceful error per acceptance §edge-cases
        setDeleteError(t('viewer.deleteTemporarilyUnavailable', 'Delete is temporarily unavailable. Refresh the page.'));
      } else {
        setDeleteError(t('viewer.deleteFailed', 'Could not delete. Please try again later.'));
      }
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div
      className="relative bg-bg-app overflow-hidden"
      style={{ minHeight: '100vh', height: '100vh' }}
      data-testid="photo-viewer"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Image container ──────────────────────────────────── */}
      <div
        className={[
          'relative flex items-center justify-center bg-bg-app overflow-hidden min-h-screen',
        ].filter(Boolean).join(' ')}
        style={{ height: '100vh' }}
        ref={imageContainerRef}
        data-testid="photo-image-container"
        data-zoomed={isZoomed ? 'true' : 'false'}
        onDoubleClick={handleDoubleClick}
      >
        {/* Main photo — full screen, letterbox on mismatch */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="block w-full h-full object-contain object-center select-none"
            style={{
              maxHeight: '100vh',
              objectPosition: 'center center',
              transform: `translate(${zoomOffset.x}px, ${zoomOffset.y}px) scale(${zoomScale})`,
              transformOrigin: 'center center',
              transition: pinchRef.current.active || panRef.current.active
                ? 'none'
                : 'transform 180ms ease-out',
              touchAction: 'none',
              cursor: isZoomed ? 'grab' : 'default',
            }}
            data-testid="photo-image"
            draggable={false}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        ) : (
          <div
            className="w-full flex items-center justify-center text-text-secondary"
            style={{ height: 400 }}
          >
            <Icon name="image" size={48} className="text-text-secondary" />
          </div>
        )}

        {imageIsLoading && imageUrl && (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-bg-app/35 backdrop-blur-[2px]"
            data-testid="photo-image-loader"
          >
            <Icon name="loader" size={32} className="text-text-primary animate-spin" />
          </div>
        )}

        {/* Top-left: Info button 32×32. Tapping it slides metadata chips out
            to the right while the button stays anchored in place. */}
        <button
          type="button"
          onClick={() => setShowInfo((open) => !open)}
          className={[
            'absolute z-30 flex items-center justify-center rounded-home-16 text-text-primary transition-opacity duration-150',
            controlsVisibilityClass,
          ].join(' ')}
          style={{
            ...VIEWER_ICON_BUTTON_STYLE,
            top: VIEWER_TOP_CONTROL_OFFSET,
            left: VIEWER_INLINE_CONTROL_INSET,
          }}
          aria-label={t('common.info', 'Info')}
          aria-pressed={showInfo}
          data-testid="info-button"
        >
          <Icon name="info" size={16} className="text-text-primary" />
        </button>

        {/* Top-right: Remove button 32×32 */}
        <button
          type="button"
          onClick={() => {
            setShowDeleteModal(true);
            setShowInfo(false);
          }}
          className={[
            'absolute z-30 flex items-center justify-center rounded-home-16 text-text-primary transition-opacity duration-150',
            controlsVisibilityClass,
          ].join(' ')}
          style={{
            ...VIEWER_ICON_BUTTON_STYLE,
            top: VIEWER_TOP_CONTROL_OFFSET,
            right: VIEWER_INLINE_CONTROL_INSET,
          }}
          aria-label={t('common.delete')}
          data-testid="remove-button"
        >
          <Icon name="delete" size={18} className="text-text-primary" />
        </button>

        {/* Floating AnimateFloatingCTA — bottom-center inside imageContainer */}
        <div
          className={[
            'absolute left-0 right-0 z-20 flex justify-center pointer-events-none transition-opacity duration-150',
            controlsVisibilityClass,
          ].join(' ')}
          style={{ bottom: bottomPanelHeight + 16 }}
        >
          <div className="pointer-events-auto">
            <AnimateFloatingCTA onClick={() => setShowReviveSheet(true)} />
          </div>
        </div>

        {/* Info Panel overlay */}
        {showInfo && !isZoomed && (
          <InfoPanel item={item} onClose={() => setShowInfo(false)} />
        )}

        {/* Delete Confirm Modal */}
        {showDeleteModal && (
          <DeleteConfirmModal
            onConfirm={handleDelete}
            onCancel={() => {
              setShowDeleteModal(false);
              setDeleteError(null);
            }}
            isDeleting={isDeleting}
          />
        )}

      </div>

      {/* ── Below image: refs + prompt + action row ─────────── */}
      <div
        ref={bottomPanelRef}
        className={[
          'flex flex-col gap-3 px-4 pt-3 pb-4',
          'transition-opacity duration-150',
          controlsVisibilityClass,
          isZoomed
            ? 'absolute bottom-0 left-0 right-0 z-20 bg-black/20 backdrop-blur-md border-t border-white/10'
            : 'absolute bottom-0 left-0 right-0 z-20 bg-bg-app',
        ].filter(Boolean).join(' ')}
        data-testid="photo-bottom-panel"
        data-zoomed={isZoomed ? 'true' : 'false'}
        style={isZoomed ? {
          background: 'rgba(18, 18, 18, 0.28)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          paddingBottom: 'calc(var(--safe-bottom, 0px) + 16px)',
        } : undefined}
      >
        {/* 8 reference thumbnails (only if refs present) */}
        {refs.length > 0 && (
          <div className="flex gap-2 flex-wrap" data-testid="reference-thumbnails">
            {refs.slice(0, 8).map((url, i) => (
              <img
                key={i}
                src={url}
                alt=""
                className="object-cover rounded-home-8"
                style={{ width: 42, height: 42 }}
              />
            ))}
          </div>
        )}

        {/* Prompt block */}
        <PromptBlock userPrompt={userPrompt} onCopy={handleCopyPrompt} />

        {/* Error message */}
        {deleteError && (
          <p className="text-text-secondary font-sans text-center" style={{ fontSize: 12 }}>
            {deleteError}
          </p>
        )}

        {/* Action Row — 3 buttons (Скачать / Поделиться / Recreate) */}
        <ActionRow
          onDownload={handleDownload}
          onShare={handleShare}
          onRepeat={handleRepeat}
        />
      </div>

      {/* [07.4] PhotoToVideoSheet — bottom-sheet «Оживить фото».
          Triggered by AnimateFloatingCTA above. Reads userPlan/balance from
          authStore so paywall (FREE+1080p) and insufficient-credits checks work. */}
      <PhotoToVideoSheet
        open={showReviveSheet}
        userPlan={userPlan}
        balance={balance}
        srcGenerationId={id}
        onClose={() => setShowReviveSheet(false)}
      />
    </div>
  );
}
